import { OrganizationLevelType } from '../enums';
import { extractHighestOrganizationLevel } from '../library/helpers/permissions/extract-highest-organization-level';
import { themedAssetUrl } from '../library/helpers/themed-asset-url';
import { CourseSmallViewModel } from '../models/overmind/learner-skill-with-courses';
import { OrganizationLevel, Permissions } from '../models/overmind/permissions';
import { User } from '../models/user';
import { Mode, PagePath } from './navigation.enums';
import { ISideNavChildRoute, ISideNavRoute } from './side-nav/side-nav.models';
import { getSubdomain } from '../library/helpers/get-subdomain';
import { LearnerAssessmentResponse } from '../overmind/actions';
import { FeatureFlagType } from '../enums/feature-flag-type';

const learnerRoutes: ISideNavRoute[] = [
  {
    icon: themedAssetUrl('icons/sidebar/home.icon.svg'),
    path: PagePath.dashboard,
    title: 'Dashboard',
    featureFlags: [FeatureFlagType.ClassicFeatures, FeatureFlagType.FreemiumSignup]
  },
  {
    icon: themedAssetUrl('icons/sidebar/skills-gaps.icon.svg'),
    path: PagePath.yourSkills,
    title: 'Your Skills',
    featureFlags: [FeatureFlagType.ClassicFeatures, FeatureFlagType.FreemiumSignup]
  },

  {
    icon: themedAssetUrl('icons/sidebar/learning-journey.icon.svg'),
    path: PagePath.learningJourneyBase,
    title: 'Learning Journey',
    children: [],
    featureFlags: [FeatureFlagType.ClassicFeatures]
  },
  {
    icon: themedAssetUrl('icons/sidebar/assessments.icon.svg'),
    path: PagePath.yourAssessmentsBase,
    title: 'Your Assessments',
    featureFlags: [FeatureFlagType.ClassicFeatures, FeatureFlagType.FreemiumSignup]
  },
];

const userManagementRoute: ISideNavRoute = {
  icon: themedAssetUrl('icons/sidebar/company.icon.svg'),
  path: PagePath.userManagementBase,
  title: 'User Management',
  featureFlags: [FeatureFlagType.ClassicFeatures],
  children: [
    {
      path: `${PagePath.userManagementBase}${PagePath.userManagementDepartments}`,
      title: 'Departments',
      exactMatch: true,
      featureFlags: [FeatureFlagType.ClassicFeatures]
    },
    {
      path: `${PagePath.userManagementBase}${PagePath.userManagementTeams}`,
      title: 'Teams',
      exactMatch: true,
      featureFlags: [FeatureFlagType.ClassicFeatures]
    },
    {
      path: `${PagePath.userManagementBase}${PagePath.userManagementPeople}`,
      title: 'People',
      exactMatch: true,
      featureFlags: [FeatureFlagType.ClassicFeatures]
    },
  ],
};

const freemiumUserManagementRoute: ISideNavRoute = {
  icon: themedAssetUrl('icons/sidebar/company.icon.svg'),
  path: PagePath.freemiumUserManagementBase,
  title: 'User Management',
  featureFlags: [FeatureFlagType.FreemiumSignup],
  children: [
    {
      path: `${PagePath.freemiumUserManagementBase}${PagePath.freemiumUserManagementPeople}`,
      title: 'People',
      exactMatch: true,
      featureFlags: [FeatureFlagType.FreemiumSignup]
    },
  ],
};

const analyticsRoute: ISideNavRoute = {
  icon: themedAssetUrl('icons/sidebar/analytics.icon.svg'),
  path: PagePath.analyticsBase,
  title: 'Analytics',
  featureFlags: [FeatureFlagType.ClassicFeatures, FeatureFlagType.FreemiumSignup],
  children: [
    {
      path: `${PagePath.analyticsBase}${PagePath.analyticsOverview}`,
      title: 'Overview',
      featureFlags: [FeatureFlagType.ClassicFeatures, FeatureFlagType.FreemiumSignup]
    },
    {
      path: `${PagePath.analyticsBase}${PagePath.analyticsComparisonTools}`,
      title: 'Comparison tools',
      featureFlags: [FeatureFlagType.ClassicFeatures, FeatureFlagType.FreemiumSignup]
    },
    {
      path: `${PagePath.analyticsBase}${PagePath.analyticsDepartments}`,
      title: 'Departments',
      featureFlags: [FeatureFlagType.ClassicFeatures]
    },
    {
      path: `${PagePath.analyticsBase}${PagePath.analyticsTeams}`,
      title: 'Teams',
      featureFlags: [FeatureFlagType.ClassicFeatures]
    },
    {
      path: `${PagePath.analyticsBase}${PagePath.analyticsPeople}`,
      title: 'People',
      featureFlags: [FeatureFlagType.ClassicFeatures, FeatureFlagType.FreemiumSignup]
    },
    {
      path: `${PagePath.analyticsBase}${PagePath.analyticsRoles}`,
      title: 'Roles',
      featureFlags: [FeatureFlagType.ClassicFeatures, FeatureFlagType.FreemiumSignup]
    },
    {
      path: `${PagePath.analyticsBase}${PagePath.analyticsSkills}`,
      title: 'Skills',
      featureFlags: [FeatureFlagType.ClassicFeatures, FeatureFlagType.FreemiumSignup]
    },
    {
      path: `${PagePath.analyticsBase}${PagePath.analyticsAssessments}`,
      title: 'Assessments',
      featureFlags: [FeatureFlagType.ClassicFeatures, FeatureFlagType.FreemiumSignup]
    },
    {
      path: `${PagePath.analyticsBase}${PagePath.analyticsEngagement}`,
      title: 'Engagement',
      featureFlags: [FeatureFlagType.ClassicFeatures]
    },
    {
      path: `${PagePath.analyticsBase}${PagePath.analyticsReportTemplates}`,
      title: 'Report templates',
      featureFlags: [FeatureFlagType.ClassicFeatures]
    },
  ],
};

const assessmentManagementRoute: ISideNavRoute = {
  icon: themedAssetUrl('icons/sidebar/assessments.icon.svg'),
  path: PagePath.assessmentManagementBase,
  title: 'Assessment Management',
  featureFlags: [FeatureFlagType.ClassicFeatures],
};

const freemiumAssessmentManagementRoute: ISideNavRoute = {
  icon: themedAssetUrl('icons/sidebar/assessments.icon.svg'),
  path: PagePath.freemiumAssessment,
  title: 'Assessment Management',
  featureFlags: [FeatureFlagType.FreemiumSignup],
};

const learningManagementRoute: ISideNavRoute = {
  icon: themedAssetUrl('icons/sidebar/learning-management.icon.svg'),
  path: PagePath.learningManagementBase,
  title: 'Learning Management',
  featureFlags: [FeatureFlagType.ClassicFeatures]
};

const reportManagementRoute: ISideNavRoute = {
  icon: themedAssetUrl('icons/sidebar/report-management.icon.svg'),
  path: PagePath.reportManagement,
  title: 'Report Management',
  featureFlags: [FeatureFlagType.ClassicFeatures]
};

const rolesSkillsManagementRoute: ISideNavRoute = {
  icon: themedAssetUrl('icons/sidebar/roles-skills.icon.svg'),
  path: PagePath.roleSkillManagementBase,
  title: 'Role & Skill Management',
  featureFlags: [FeatureFlagType.ClassicFeatures, FeatureFlagType.FreemiumSignup],
  children: [
    {
      path: `${PagePath.roleSkillManagementBase}${PagePath.roleSkillManagementAllRoles}`,
      title: 'Manage roles',
      exactMatch: true,
      featureFlags: [FeatureFlagType.ClassicFeatures, FeatureFlagType.FreemiumSignup]
    },
    {
      path: `${PagePath.roleSkillManagementBase}${PagePath.roleSkillManagementAllSkills}`,
      title: 'Manage skills',
      exactMatch: true,
      featureFlags: [FeatureFlagType.ClassicFeatures, FeatureFlagType.FreemiumSignup]
    },
    {
      path: `${PagePath.roleSkillManagementBase}${PagePath.roleSkillManagementSkillCategories}`,
      title: 'Manage skill categories',
      exactMatch: true,
      featureFlags: [FeatureFlagType.ClassicFeatures, FeatureFlagType.FreemiumSignup]
    },
  ],
};

const aiManagementRoute: ISideNavRoute = {
  icon: themedAssetUrl('icons/sidebar/ai-agent.icon.svg'),
  path: PagePath.aiManagementBase,
  title: 'AI Management',
  featureFlags: [FeatureFlagType.ClassicFeatures],
  children: [
    {
      path: `${PagePath.aiManagementBase}${PagePath.aiManagementAgents}`,
      title: 'Agents',
      exactMatch: true,
      featureFlags: [FeatureFlagType.ClassicFeatures]
    },
    {
      path: `${PagePath.aiManagementBase}${PagePath.aiManagementContexts}`,
      title: 'Contexts',
      exactMatch: true,
      featureFlags: [FeatureFlagType.ClassicFeatures]
    },
    {
      path: `${PagePath.aiManagementBase}${PagePath.aiManagementInformationSearch}`,
      title: 'Information Search',
      exactMatch: true,
      featureFlags: [FeatureFlagType.ClassicFeatures]
    },
  ],
};

export function generateLearnerRoutes(user: User | undefined, forcedAssessments: LearnerAssessmentResponse[], yearCourses: CourseSmallViewModel[], assignedCourses: CourseSmallViewModel[]): ISideNavRoute[] {
  if (!user?.completedOnboarding || !!forcedAssessments.length) return [];

  const routes: ISideNavRoute[] = structuredClone(learnerRoutes);

  const subdomain = getSubdomain();

  // lpi
  if (subdomain.includes('lpi')) {
    routes.forEach(route => {
      if (route.title === 'Your Skills') route.title = 'My Skills Report';
      if (route.title === 'Learning Journey') route.title = 'Level Up';
      if (route.title === 'Your Assessments') route.title = 'My Assessments';
    });
  }

  if (!!user?.jobRoles?.length) {
    routes.push({
      icon: themedAssetUrl('icons/sidebar/career-pathways.icon.svg'),
      title: 'Your Career',
      path: PagePath.yourCareerBase,
      featureFlags: [FeatureFlagType.ClassicFeatures]
    });
  }

  const learningJourneySideNavParentRoute = routes.find(route => route.path === PagePath.learningJourneyBase);
  if (!learningJourneySideNavParentRoute) return routes;

  if (yearCourses.length > 0 || assignedCourses.length > 0 || user?.allowOffPlatformLearning || user?.allowReflections || user?.allowCertificateDownload) {
    learningJourneySideNavParentRoute.children?.push({
      path: PagePath.learningJourneyBase,
      title: subdomain.includes('roche-deep') ? 'Core Skills' : subdomain.includes('lpi') ? 'My Skills' : 'Learning',
      exactMatch: true,
      featureFlags: [FeatureFlagType.ClassicFeatures]
    });
  }

  if (yearCourses.length > 0) {
    yearCourses.forEach(yearCourse => {
      if (learningJourneySideNavParentRoute.children?.some(child => child.title === yearCourse.displayName)) return;

      learningJourneySideNavParentRoute.children?.push({
        path: `${PagePath.learningJourneyBase}${PagePath.learningJourneyYearSkill.replace(':skillSlug', yearCourse.slug)}`,
        title: yearCourse.displayName,
        exactMatch: true,
        featureFlags: [FeatureFlagType.ClassicFeatures]
      });
    });
  }

  if (assignedCourses.length > 0) {
    assignedCourses.forEach(assignedCourse => {
      if (learningJourneySideNavParentRoute.children?.some(child => child.title === assignedCourse.displayName)) return;

      learningJourneySideNavParentRoute.children?.push({
        path: `${PagePath.learningJourneyBase}${PagePath.learningJourneyAssignedSkill.replace(':skillSlug', assignedCourse.slug)}`,
        title: assignedCourse.displayName,
        exactMatch: true,
        featureFlags: [FeatureFlagType.ClassicFeatures]
      });
    });
  }

  if (user?.allowOffPlatformLearning) {
    learningJourneySideNavParentRoute.children?.push({
      path: `${PagePath.learningJourneyBase}${PagePath.learningJourneyAdditionalLearning}`,
      title: 'Additional learning',
      exactMatch: true,
      featureFlags: [FeatureFlagType.ClassicFeatures]
    });
  }

  if (user?.allowReflections) {
    learningJourneySideNavParentRoute.children?.push({
      path: `${PagePath.learningJourneyBase}${PagePath.learningJourneyCourseReflections}`,
      title: 'Reflections',
      exactMatch: true,
      featureFlags: [FeatureFlagType.ClassicFeatures]
    });
  }

  if (user?.allowCertificateDownload) {
    learningJourneySideNavParentRoute.children?.push({
      path: `${PagePath.learningJourneyBase}${PagePath.learningJourneyCertificates}`,
      title: 'Certificates',
      exactMatch: true,
      featureFlags: [FeatureFlagType.ClassicFeatures]
    });
  }

  return routes;
}

function populateCompanySlugs(currentRoutes: ISideNavRoute[], companySlug: string) {
  const nextRoutes: ISideNavRoute[] = structuredClone(currentRoutes);
  nextRoutes.forEach(parentRoute => {
    parentRoute.path = parentRoute.path.replace(`:companySlug`, companySlug);
    parentRoute.children?.forEach(childRoute => {
      childRoute.path = childRoute.path.replace(`:companySlug`, companySlug);
    });
  });
  return nextRoutes;
}

function removeChildRoute(currentChildren: ISideNavChildRoute[] | undefined, title: string): ISideNavChildRoute[] {
  if (!currentChildren) return [];
  const nextChildren = structuredClone(currentChildren);
  const childIndex = currentChildren.findIndex(child => child.title === title);
  nextChildren.splice(childIndex, 1);
  return nextChildren;
}

function generateAdminRoute(organizationLevel: OrganizationLevel | undefined): ISideNavRoute | undefined {
  if (!organizationLevel || organizationLevel.organizationLevel === OrganizationLevelType.Learner) return undefined;

  const route: ISideNavRoute = structuredClone(userManagementRoute);
  route.children = removeChildRoute(route.children, 'Report Management');

  switch (organizationLevel.organizationLevel) {
    case OrganizationLevelType.Company: {
      return route;
    }

    case OrganizationLevelType.Department: {
      route.children = removeChildRoute(route.children, 'Tags');

      if (organizationLevel.entities.length > 1) return route;

      route.children = removeChildRoute(route.children, 'Departments');
      return route;
    }

    case OrganizationLevelType.Team: {
      route.children = removeChildRoute(route.children, 'Departments');
      route.children = removeChildRoute(route.children, 'Tags');

      if (organizationLevel.entities.length > 1) return route;

      route.children = removeChildRoute(route.children, 'Teams');
      return route;
    }

    default: {
      return undefined;
    }
  }
}

function generateAnalyticsRoutes(organizationLevel: OrganizationLevel | undefined): ISideNavRoute | undefined {
  if (!organizationLevel || organizationLevel.organizationLevel === OrganizationLevelType.Learner) return undefined;

  const route: ISideNavRoute = structuredClone(analyticsRoute);

  switch (organizationLevel.organizationLevel) {
    case OrganizationLevelType.Company: {
      return route;
    }

    case OrganizationLevelType.Department: {
      route.children = removeChildRoute(route.children, 'Comparison tools');
      route.children = removeChildRoute(route.children, 'Report templates');

      if (organizationLevel.entities.length > 1) return route;

      route.children = removeChildRoute(route.children, 'Departments');
      return route;
    }

    case OrganizationLevelType.Team: {
      route.children = removeChildRoute(route.children, 'Comparison tools');
      route.children = removeChildRoute(route.children, 'Departments');
      route.children = removeChildRoute(route.children, 'Assessments');

      if (organizationLevel.entities.length > 1) return route;

      route.children = removeChildRoute(route.children, 'Teams');
      return route;
    }

    default: {
      return undefined;
    }
  }
}

function generateLearningManagementRoutes(organizationLevel: OrganizationLevel | undefined): ISideNavRoute | undefined {
  if (!organizationLevel || organizationLevel.organizationLevel === OrganizationLevelType.Learner) return undefined;

  const route: ISideNavRoute = structuredClone(learningManagementRoute);
  return route;
}

function generateAssessmentManagementRoutes(organizationLevel: OrganizationLevel | undefined): ISideNavRoute | undefined {
  if (!organizationLevel || organizationLevel.organizationLevel === OrganizationLevelType.Learner) return undefined;

  const route: ISideNavRoute = structuredClone(assessmentManagementRoute);
  return route;
}

function generateRoleManagementRoutes(organizationLevel: OrganizationLevel | undefined): ISideNavRoute | undefined {
  if (!organizationLevel || (organizationLevel.organizationLevel !== OrganizationLevelType.Company && organizationLevel.organizationLevel !== OrganizationLevelType.Department)) return undefined;

  const route: ISideNavRoute = structuredClone(rolesSkillsManagementRoute);
  return route;
}

export function generateCompanyRoutes(user: User | undefined, forcedAssessments: LearnerAssessmentResponse[], activeCompanySlug: string | undefined, mode: Mode, permissions: Permissions | undefined, isFreemium: boolean): ISideNavRoute[] {
  const companySlug = activeCompanySlug ?? user?.companySlug;
  let routes: ISideNavRoute[] = [];
  if (!user?.completedOnboarding || !!forcedAssessments.length || !companySlug || (user?.isSystemAdmin && mode === Mode.UserDashboard)) return routes;

  if (user?.isSystemAdmin && mode === Mode.AdminDashboard) {
    routes = routes.concat(userManagementRoute, analyticsRoute, assessmentManagementRoute, learningManagementRoute, reportManagementRoute, rolesSkillsManagementRoute, aiManagementRoute);
    return populateCompanySlugs(routes, companySlug);
  }

  if (!!permissions?.administration) {
    const organizationLevel = extractHighestOrganizationLevel(permissions?.administration.organizationLevels);
    const route = generateAdminRoute(organizationLevel);
    if (!!route) routes = routes.concat(route);
  }

  if (!!permissions?.analytics) {
    const organizationLevel = extractHighestOrganizationLevel(permissions.analytics.organizationLevels);
    const route = generateAnalyticsRoutes(organizationLevel);
    if (!!route) routes = routes.concat(route);
  }

  if (isFreemium) {
    routes = routes.concat(freemiumUserManagementRoute);
    routes = routes.concat(freemiumAssessmentManagementRoute);
  }

  if (!!permissions?.learningManagement) {
    const organizationLevel = extractHighestOrganizationLevel(permissions.learningManagement.organizationLevels);
    const route = generateLearningManagementRoutes(organizationLevel);
    if (!!route) routes = routes.concat(route);
  }

  if (!!permissions?.assessments) {
    const organizationLevel = extractHighestOrganizationLevel(permissions.assessments.organizationLevels);
    const route = generateAssessmentManagementRoutes(organizationLevel);
    if (!!route) routes = routes.concat(route);
  }

  if (!!permissions?.roleSkillManagement) {
    const organizationLevel = extractHighestOrganizationLevel(permissions.roleSkillManagement.organizationLevels);
    const route = generateRoleManagementRoutes(organizationLevel);
    if (!!route) routes = routes.concat(route);
  }

  return populateCompanySlugs(routes, companySlug);
}

export function keplerActive(exactMatch: boolean, path: string, pathname: string): boolean {
  if (exactMatch && pathname === path) return true;
  if (!exactMatch && pathname.startsWith(path)) return true;
  return false;
}

